import React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { cn } from "../lib/cn";

interface ModalProps {
  show: boolean;
  onHide: () => void;
  children: React.ReactNode;
  size?: "sm" | "lg" | "xl" | "full";
  centered?: boolean;
  scrollable?: boolean;
  backdrop?: boolean | "static";
}

interface ModalSectionProps extends React.HTMLAttributes<HTMLDivElement> {
  closeButton?: boolean;
}

const ModalRoot = ({
  show,
  onHide,
  children,
  size,
  centered = true,
  scrollable = false,
  backdrop,
}: ModalProps) => (
  <Dialog.Root open={show} onOpenChange={(open) => !open && onHide()}>
    <Dialog.Portal>
      <Dialog.Overlay className="modal-overlay" />
      <Dialog.Content
        className={cn(
          "modal-content",
          size && `modal-${size}`,
          centered && "modal-centered",
          scrollable && "modal-scrollable",
        )}
        aria-describedby={undefined}
        onPointerDownOutside={backdrop === "static" ? (event) => event.preventDefault() : undefined}
      >
        {children}
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>
);

const ModalHeader = ({ closeButton, className, children, ...props }: ModalSectionProps) => {
  return (
    <div className={cn("modal-header", className)} {...props}>
      {children}
      {closeButton && (
        <Dialog.Close asChild>
          <button type="button" className="modal-close" aria-label="닫기">
            <X aria-hidden="true" />
          </button>
        </Dialog.Close>
      )}
    </div>
  );
};

const ModalTitle = ({ className, children, ...props }: ModalSectionProps) => (
  <Dialog.Title className={cn("modal-title", className)} {...props}>
    {children}
  </Dialog.Title>
);

const ModalBody = ({ className, ...props }: ModalSectionProps) => (
  <div className={cn("modal-body", className)} {...props} />
);

const ModalFooter = ({ className, ...props }: ModalSectionProps) => (
  <div className={cn("modal-footer", className)} {...props} />
);

type ModalComponent = typeof ModalRoot & {
  Header: typeof ModalHeader;
  Title: typeof ModalTitle;
  Body: typeof ModalBody;
  Footer: typeof ModalFooter;
};

const Modal = ModalRoot as ModalComponent;
Modal.Header = ModalHeader;
Modal.Title = ModalTitle;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;

export default Modal;
